import React, { useState, useEffect } from 'react';
import './CSVImport.css';
import Icon from './Icon';
import SaveConsent from './SaveConsent';
import RosterPreviewStudents from './RosterPreviewStudents';
import { usePageFileDrop, PageDropOverlay } from './PageFileDrop';
import PeriodBar, { usePeriod } from './PeriodBar';
import { useImportSources } from '../hooks/useUniversitySettings';

// 取り込みの種類ごとに「何がサーバーに残るか」と、確認の既定値【2026-09-11】
// 名簿だけ既定ON。名簿が無いと出席率の分母が作れず、システムが成り立たない。
// 残りは既定OFFで、置くなら毎回意識して置いてもらう。
const SAVE_INFO = {
  manaba: {
    defaultOn: true,
    title: '履修者名簿をサーバーに保存します',
    items: [
      '学籍番号・氏名（manabaの正式表記）・氏名(英語)・メールアドレス',
      'コース名と曜日時限。授業が無ければ新しく作られます',
      'アプリ未登録の学生には仮アカウントが作られます',
    ],
    note: '出席率は「打刻数 ÷（実施回数 × 履修者数）」で出すので、名簿が無いと数字が作れません。',
  },
  attendance: {
    defaultOn: false,
    title: '出席記録をサーバーに保存します',
    items: ['学籍番号・氏名・打刻日時・出欠の状態'],
    note: '既に同じ回の記録があれば上書きされます。',
  },
  users: {
    defaultOn: false,
    title: 'ユーザーをサーバーに保存します',
    items: ['学籍番号・氏名・メールアドレス・初期パスワード'],
    note: 'アカウントが新しく作られます。取り消すには個別に削除が必要です。',
  },
};

// only="manaba" を渡すと履修者名簿の取り込みだけを出す【2026-09-07】
// 学生管理の中から使うためのモード。種類の切り替えは出さない。
const CSVImport = ({ user, only = null, embedded = false }) => {
  const [dragActive, setDragActive] = useState(false);
  const [file, setFile] = useState(null);
  const [allClasses, setClasses] = useState([]);
  // 取り込み先の授業は今の期間の授業だけ出す【2026-09-17】
  const period = usePeriod();
  const classes = period.filter(Array.from(new Map(allClasses.map(c => [c.id, c])).values()));
  const [selectedClass, setSelectedClass] = useState('');
  // 'attendance' | 'users' | 'manaba'（manaba = 履修者名簿）【2026-09-04】
  const [importType, setImportType] = useState(only || 'attendance');
  // 大学で使う取り込み方法だけ出す【2026-11-04】（サーバーの入口でも止めている）
  const sources = useImportSources();
  const SOURCE_OF = { attendance: 'attendance_csv', users: 'users_csv', manaba: 'manaba' };
  const typeOn = (t) => sources.on(SOURCE_OF[t]);
  useEffect(() => {
    if (only || typeOn(importType)) return;
    const next = ['attendance', 'users', 'manaba'].find(typeOn);
    if (next) setImportType(next);
  }, [sources.loaded]); // eslint-disable-line react-hooks/exhaustive-deps
  const [teachers, setTeachers] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState('');
  // 年度・学期【2026-09-14】
  //   manabaのCSVには年度の列が無いので、ここで選んでもらう。
  //   学期の選択肢は大学ごとのマスタ（/api/terms）から引く。
  //   二期制・三期制・クォーター制の違いはサーバー側のデータの違いでしかないので、
  //   画面に '前期'/'後期' をベタ書きしないこと。
  const [terms, setTerms] = useState([]);
  const [yearOptions, setYearOptions] = useState([]);
  const [academicYear, setAcademicYear] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  // 名簿は「確認 → 取り込む」の2段にする【2026-09-17】
  //   確認（dryRun）の結果。コースごとに誰が入るかを一覧で見せてから保存する。
  const [preview, setPreview] = useState(null);
  const [openCourse, setOpenCourse] = useState(null);
  const [error, setError] = useState(null);
  // 保存してよいかの確認【2026-09-11】。種類を変えたら既定に戻す
  const [saveOk, setSaveOk] = useState(
    (SAVE_INFO[only || 'attendance'] || {}).defaultOn || false
  );
  useEffect(() => {
    setSaveOk((SAVE_INFO[importType] || {}).defaultOn || false);
  }, [importType]);

  // 教員本人がアップロードするときは自分の授業になるので担当者選択は不要。
  // 教務・開発者が代理で投入するときだけ「誰の授業か」を選ばせる。
  const needsTeacherPick = user && user.role !== 'teacher';

  // 授業一覧を取得
  useEffect(() => {
    if (period.ready) fetchClasses();
  }, [period.ready, period.year]); // eslint-disable-line react-hooks/exhaustive-deps

  // 選んでいる授業が期間の外になったら、期間の最初の授業にする
  const shownIds = classes.map(c => String(c.id)).join(',');
  useEffect(() => {
    if (classes.length === 0) { setSelectedClass(''); return; }
    if (!classes.some(c => String(c.id) === String(selectedClass))) setSelectedClass(classes[0].id);
  }, [shownIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // 担当教員の候補（教務が代理投入する場合のみ）
  useEffect(() => {
    if (importType !== 'manaba' || !needsTeacherPick || teachers.length > 0) return;
    fetchTeachers();
  }, [importType, needsTeacherPick]); // eslint-disable-line react-hooks/exhaustive-deps

  // 学期マスタと既定値（今日の年度・学期）
  useEffect(() => {
    if (importType !== 'manaba' || terms.length > 0) return;
    fetchTerms();
  }, [importType]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTerms = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/terms', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (!data.success) return;
      setTerms(data.terms || []);
      setYearOptions(data.yearOptions || []);
      // 既定値はサーバーが出す（4月始まりの現在年度と、今日を含む学期）
      if (data.defaults) {
        setAcademicYear(String(data.defaults.academicYear ?? ''));
        setSelectedTerm(data.defaults.termId ? String(data.defaults.termId) : '');
      }
    } catch (err) {
      console.error('学期の取得に失敗:', err);
    }
  };

  const fetchTeachers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/csv-import/teachers', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setTeachers(data.teachers);
        if (data.teachers.length > 0) setSelectedTeacher(String(data.teachers[0].id));
      }
    } catch (error) {
      console.error('教員一覧取得エラー:', error);
    }
  };

  const fetchClasses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/schedule/classes' + (period.year ? '?academicYear=' + period.year : ''), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setClasses(data.classes);
      }
    } catch (error) {
      console.error('授業取得エラー:', error);
    }
  };

  // ドラッグ&ドロップ処理
  const handleDrag = (e) => {
    e.preventDefault();
    // stopPropagation はしない（画面全体の受け口がドラッグ中の表示を消せなくなる）
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    // stopPropagation はしない（画面全体の受け口がドラッグ中の表示を消せなくなる）
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  // ファイル選択処理
  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  // 年度・学期・担当・ファイルが変わったら、確認結果は古くなるので捨てる
  useEffect(() => { setPreview(null); setOpenCourse(null); },
    [academicYear, selectedTerm, selectedTeacher, file, importType]);

  // ファイル処理
  const handleFile = (selectedFile) => {
    // 名簿は manaba の xlsx もそのまま読める（サーバー側で対応済み）
    const okExt = importType === 'manaba' ? /\.(csv|tsv|xls|xlsx|xlsm|ods)$/i : /\.csv$/i;
    if (!okExt.test(selectedFile.name)) {
      setError(importType === 'manaba'
        ? 'CSV か xlsx を選んでください'
        : 'CSVファイルのみアップロード可能です');
      return;
    }

    if (selectedFile.size > 10 * 1024 * 1024) {
      setError('ファイルサイズは10MB以下にしてください');
      return;
    }

    setFile(selectedFile);
    setError(null);
    setResult(null);
  };

  // 画面のどこに落としても受ける【2026-09-17】（ページとして開いているときだけ）
  const pageDragging = usePageFileDrop((files) => handleFile(files[0]), { enabled: !embedded });

  // アップロード処理
  //   名簿は、まず確認（dryRun）して一覧を見せる。confirm=true で本当に保存する。
  const handleUpload = async (opts = {}) => {
    const dryRun = importType === 'manaba' && opts.confirm !== true;
    if (!file) {
      setError('ファイルを選択してください');
      return;
    }

    if (importType === 'attendance' && !selectedClass) {
      setError('授業を選択してください');
      return;
    }

    if (importType === 'manaba' && needsTeacherPick && !selectedTeacher) {
      setError('担当教員を選択してください');
      return;
    }

    if (!saveOk) {
      setError('サーバーに保存することを確認してください');
      return;
    }

    setUploading(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      
      if (importType === 'attendance') {
        formData.append('classId', selectedClass);
      }

      if (importType === 'manaba' && needsTeacherPick) {
        formData.append('teacherId', selectedTeacher);
      }

      // 年度・学期。未選択ならサーバー側が「今日の年度・学期」に落とす
      if (importType === 'manaba') {
        if (academicYear) formData.append('academicYear', academicYear);
        if (selectedTerm) formData.append('termId', selectedTerm);
        if (dryRun) formData.append('dryRun', '1');
      }

      const token = localStorage.getItem('token');
      const ENDPOINTS = {
        attendance: '/api/csv-import/attendance',
        users:      '/api/csv-import/users',
        manaba:     '/api/csv-import/manaba',
      };
      const endpoint = ENDPOINTS[importType];

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (data.success && dryRun) {
        // 確認だけ。ファイルはそのまま残して「この内容で取り込む」を待つ
        setPreview(data.result);
        setOpenCourse(null);
      } else if (data.success) {
        setResult(data);
        setPreview(null);
        setFile(null);
        
        // 成功時にページをリロード（最新データを表示）
        // manaba は取り込み結果（氏名の更新履歴など）を読ませたいのでリロードしない
        if (importType !== 'manaba') {
          setTimeout(() => {
            window.location.reload();
          }, 3000);
        }
      } else {
        setError(data.message || 'インポートに失敗しました');
      }

    } catch (error) {
      console.error('アップロードエラー:', error);
      setError('アップロード中にエラーが発生しました');
    } finally {
      setUploading(false);
    }
  };

  // ファイルクリア
  const handleClear = () => {
    setFile(null);
    setError(null);
    setResult(null);
    setPreview(null);
  };

  // 確認結果（取り込む前）【2026-09-17】
  const renderPreview = () => {
    if (!preview) return null;
    const p = preview;
    return (
      <div style={{ border: '1px solid #fcd34d', background: '#fffbeb', borderRadius: 10,
                    padding: '0.9rem 1rem', margin: '1rem 0' }}>
        <div style={{ fontWeight: 700, color: '#92400e', marginBottom: '0.4rem' }}>
          取り込む前の確認（まだ保存されていません）
        </div>
        <div style={{ fontSize: '0.88rem', lineHeight: 1.8 }}>
          {p.academicYear}年度{p.termName || ''} ・ ファイル {p.totalRecords}行 ・
          新しく履修 <strong>{p.enrolled}</strong>人 ・ 仮アカウント作成 <strong>{p.usersCreated}</strong>人
          {p.namesUpdated > 0 && <> ・ 氏名が変わる {p.namesUpdated}人</>}
          {p.skipped > 0 && <> ・ 取り込まない {p.skipped}行</>}
          {p.errorCount > 0 && <span style={{ color: '#b91c1c', fontWeight: 700 }}> ・ エラー {p.errorCount}行</span>}
        </div>
        {p.migrationWarning && (
          <div style={{ fontSize: '0.8rem', color: '#b45309' }}>⚠ {p.migrationWarning}</div>
        )}
        {p.studentRowsTruncated && (
          <div style={{ fontSize: '0.8rem', color: '#b45309' }}>行数が多いため、一覧は途中までです</div>
        )}

        <table className="data-table" style={{ marginTop: '0.6rem', background: '#fff' }}>
          <thead>
            <tr><th>コース名</th><th style={{ textAlign: 'right' }}>行</th><th style={{ textAlign: 'right' }}>新しく履修</th><th>取り込み先</th><th></th></tr>
          </thead>
          <tbody>
            {(p.byClass || []).map(b => {
              const isOpen = openCourse === b.className;
              return (
                <React.Fragment key={b.className}>
                  <tr>
                    <td>
                      {b.className}
                      {b.otherYears && b.otherYears.length > 0 && (
                        <div style={{ fontSize: '0.75rem', color: '#b91c1c', fontWeight: 700 }}>
                          ⚠ 同じ名前の授業が {b.otherYears.join('、')} にもあります。年度は合っていますか？
                        </div>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>{b.rows + b.skipped + b.errorCount}</td>
                    <td style={{ textAlign: 'right' }}>{b.enrolled}</td>
                    <td style={{ fontSize: '0.82rem' }}>
                      {b.isNew
                        ? <span style={{ color: '#166534', fontWeight: 700 }}>新しく授業を作る</span>
                        : <span>既存の授業に追加</span>}
                    </td>
                    <td>
                      {b.students && b.students.length > 0 && (
                        <button type="button" className="btn-secondary"
                                style={{ fontSize: '0.78rem', padding: '0.1rem 0.5rem' }}
                                onClick={() => setOpenCourse(isOpen ? null : b.className)}>
                          学生の一覧 {isOpen ? '▲' : '▼'}
                        </button>
                      )}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={5}><RosterPreviewStudents students={b.students} /></td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>

        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.8rem', flexWrap: 'wrap' }}>
          <button className="btn-primary" disabled={uploading || !saveOk}
                  onClick={() => handleUpload({ confirm: true })}>
            {uploading ? '取り込み中…' : 'この内容で取り込む'}
          </button>
          <button className="btn-secondary" disabled={uploading} onClick={() => setPreview(null)}>
            やめる
          </button>
          <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>
            コースごとに取り込み先を選びたいときは、授業のページの「受講者」から取り込んでください
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="csv-import-container">
      <PageDropOverlay show={pageDragging}
                       title="ファイルをドロップして読み込む"
                       kinds={importType === 'manaba' ? ['CSV', 'XLSX'] : ['CSV']}
                       note={importType === 'manaba' ? '履修者名簿として読み込みます（中身を確認してから保存します）'
                           : importType === 'attendance' ? '出席記録CSVとして読み込みます' : 'ユーザーCSVとして読み込みます'} />
      <div className="csv-import-header">
        <h2>{only === 'manaba' ? '履修者名簿(manaba)の取り込み' : 'CSVファイルインポート'}</h2>
        <p className="csv-import-description">
          CSVファイルをドラッグ&ドロップするか、クリックして選択してください
        </p>
      </div>

      {/* インポートタイプ選択（名簿専用モードでは出さない） */}
      <div className="import-type-selector" style={only ? { display: 'none' } : undefined}>
        {typeOn('attendance') && <button
          className={`type-button ${importType === 'attendance' ? 'active' : ''}`}
          onClick={() => setImportType('attendance')}
        >
          出席記録
        </button>}
        {typeOn('users') && <button
          className={`type-button ${importType === 'users' ? 'active' : ''}`}
          onClick={() => setImportType('users')}
        >
          ユーザー
        </button>}
        {typeOn('manaba') && <button
          className={`type-button ${importType === 'manaba' ? 'active' : ''}`}
          onClick={() => setImportType('manaba')}
        >
          履修者名簿(manaba)
        </button>}
      </div>
      {!typeOn(importType) && (
        <p className="csv-import-description" style={{ color: '#b45309' }}>
          この大学では、この取り込みを使わない設定になっています（教務の「大学の設定」で変えられます）。
        </p>
      )}

      {/* 履修者名簿の説明 */}
      {importType === 'manaba' && (
        <div className="manaba-notice">
          <p>
            manabaの「コースメンバー」CSVをそのまま投入できます。授業・曜日時限・履修者が一度に登録されます。
          </p>
          <p>
            <strong>CSVの「コース名」ごとに授業へ振り分けます。</strong>
            1つのファイルに複数の授業が混ざっていても構いません。同じ名前の授業が既にあればそこへ、
            無ければ新しく作ります。取り込みの結果は授業ごとに分けて出します。
            特定の授業へまとめて入れたいときは、出席記録の画面から取り込んでください。
          </p>
          <p>
            <strong>同じ科目名でも、年度・学期が違えば別の授業として作られます。</strong>
            取り込んだあとで年度を間違えていたと気づいたら、取り込み履歴の画面から
            その取り込みごとまとめて直せます。
          </p>
          <p>
            アプリのアカウントが無い学生はログイン不可の仮アカウント（アカウント未作成）で作られ、本人がサインアップした時点で自動的に引き継がれます。
            すでに登録済みの学生は、表示名を名簿の氏名に合わせます（本人が入力した氏名は記録として残ります）。
            教務・教員が手で直した氏名はそのまま据え置きます。ログインIDは変わりません。
          </p>
        </div>
      )}

      {/* 年度・学期の選択【2026-09-14】
          manabaのCSVには年度が入っていないので、ここで決める。
          既定は今日の年度・学期なので、普段は触らずそのまま取り込める。 */}
      {importType === 'manaba' && (
        <div className="class-selector">
          <label htmlFor="year-select">年度:</label>
          <select
            id="year-select"
            value={academicYear}
            onChange={(e) => setAcademicYear(e.target.value)}
            className="class-select"
          >
            {yearOptions.length === 0 && <option value="">（読み込み中）</option>}
            {yearOptions.map((y) => (
              <option key={y} value={y}>{y}年度</option>
            ))}
          </select>

          <label htmlFor="term-select">学期:</label>
          <select
            id="term-select"
            value={selectedTerm}
            onChange={(e) => setSelectedTerm(e.target.value)}
            className="class-select"
          >
            {terms.length === 0 && <option value="">（読み込み中）</option>}
            {terms.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* 担当教員の選択（教務が代理で投入する場合のみ） */}
      {importType === 'manaba' && needsTeacherPick && (
        <div className="class-selector">
          <label htmlFor="teacher-select">担当教員:</label>
          <select
            id="teacher-select"
            value={selectedTeacher}
            onChange={(e) => setSelectedTeacher(e.target.value)}
            className="class-select"
          >
            {teachers.length === 0 && <option value="">（教員が登録されていません）</option>}
            {teachers.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}（{t.student_id}）
              </option>
            ))}
          </select>
        </div>
      )}

      {/* 授業選択（出席記録の場合のみ） */}
      {importType === 'attendance' && (
        <div className="class-selector">
          <label htmlFor="class-select">授業を選択:</label>
          <PeriodBar period={period} />
          <select
            id="class-select"
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
            className="class-select"
          >
            {classes.length === 0 && <option value="">この期間の授業がありません</option>}
            {classes.map((cls) => (
              <option key={cls.id} value={cls.id}>
                {cls.name} ({cls.semester || '学期未設定'})
              </option>
            ))}
          </select>
        </div>
      )}

      {/* ドラッグ&ドロップエリア */}
      <div
        className={`dropzone ${dragActive ? 'active' : ''} ${file ? 'has-file' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => document.getElementById('file-input').click()}
      >
        <input
          id="file-input"
          type="file"
          accept=".csv,.tsv,.xls,.xlsx,.xlsm"
          onChange={handleFileInput}
          style={{ display: 'none' }}
        />

        {!file ? (
          <div className="dropzone-content">
            <div className="dropzone-icon"><Icon name="folder" size={40}/></div>
            <p className="dropzone-text">
              CSVファイルをここにドロップ
            </p>
            <p className="dropzone-subtext">
              または、クリックしてファイルを選択
            </p>
            <div className="dropzone-formats">
              対応形式: .csv (最大10MB)
            </div>
          </div>
        ) : (
          <div className="file-info">
            <div className="file-icon"><Icon name="file-text" size={28}/></div>
            <div className="file-details">
              <p className="file-name">{file.name}</p>
              <p className="file-size">
                {(file.size / 1024).toFixed(2)} KB
              </p>
            </div>
            <button
              className="file-remove"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
            >
              ✕
            </button>
          </div>
        )}
      </div>

      {/* CSV形式のヒント */}
      <div className="csv-format-hint">
        <h4>{importType === 'attendance' ? '出席記録' : importType === 'manaba' ? '履修者名簿' : 'ユーザー'}CSVの形式</h4>
        {importType === 'manaba' ? (
          <div className="format-example">
            <code>
              コース名,学籍番号,氏名,学期,曜日時限,役割
            </code>
            <p className="format-note">
              ※ 列名は大学ごとの表記ゆれに対応しています（例: 科目名/授業名、ユーザID/ログインID、曜時限 など）
            </p>
            <p className="format-note">
              ※ 「役割」列に教員・TAとある行は自動でスキップされます
            </p>
            <p className="format-note">
              ※ Shift-JISエンコーディングに対応しています
            </p>
          </div>
        ) : importType === 'attendance' ? (
          <div className="format-example">
            <code>
              日時,学籍番号,氏名,セッション開始時刻,モード,時刻同期,セッション番号,サービスコード
            </code>
            <p className="format-note">
              ※ Shift-JISエンコーディングに対応しています
            </p>
          </div>
        ) : (
          <div className="format-example">
            <code>
              student_id,name,email,password,role
            </code>
            <p className="format-note">
              ※ role は空か student（CSV で作れるのは学生だけです。教員・教務のアカウントは作れません）
            </p>
            {/* 【2026-11-02 セキュリティ S1】すでにいる人は書き換えない */}
            <p className="format-note">
              ※ 新しい人を作るだけです。すでに登録されている学籍番号の行は、何も変えずに飛ばします（メール・氏名・役割・パスワードも変えません）
            </p>
          </div>
        )}
      </div>

      {/* エラー表示 */}
      {error && (
        <div className="alert alert-error">
          <span className="alert-icon"><Icon name="alert-triangle" size={18}/></span>
          <span className="alert-message">{error}</span>
        </div>
      )}

      {/* 結果表示（履修者名簿）【2026-09-04】 */}
      {result && importType === 'manaba' && result.result && (
        <div className="alert alert-success">
          <span className="alert-icon"><Icon name="check" size={18}/></span>
          <div className="result-details">
            <p className="alert-message">履修者名簿の取り込みが完了しました</p>
            <div className="result-stats">
              <div className="stat-item">
                <span className="stat-label">総レコード数:</span>
                <span className="stat-value">{result.result.totalRecords}</span>
              </div>
              <div className="stat-item success">
                <span className="stat-label">授業を作成:</span>
                <span className="stat-value">{result.result.classesCreated}</span>
              </div>
              <div className="stat-item info">
                <span className="stat-label">既存の授業に追加:</span>
                <span className="stat-value">{result.result.classesReused}</span>
              </div>
              <div className="stat-item success">
                <span className="stat-label">履修登録:</span>
                <span className="stat-value">{result.result.enrolled}</span>
              </div>
              <div className="stat-item info">
                <span className="stat-label">仮アカウント作成:</span>
                <span className="stat-value">{result.result.usersCreated}</span>
              </div>
              <div className="stat-item info">
                <span className="stat-label">名簿の氏名を記録:</span>
                <span className="stat-value">{result.result.rosterNamesRecorded ?? 0}</span>
              </div>
              <div className="stat-item info">
                <span className="stat-label">表示名を名簿に更新:</span>
                <span className="stat-value">{result.result.namesUpdated ?? 0}</span>
              </div>
              <div className="stat-item info">
                <span className="stat-label">手動修正のため据え置き:</span>
                <span className="stat-value">{result.result.namesKept ?? 0}</span>
              </div>
              <div className="stat-item success">
                <span className="stat-label">仮アカウントを自動統合:</span>
                <span className="stat-value">{result.result.autoMerged ?? 0}</span>
              </div>
              <div className="stat-item warning">
                <span className="stat-label">スキップ:</span>
                <span className="stat-value">{result.result.skipped}</span>
              </div>
              {result.result.errorCount > 0 && (
                <div className="stat-item error">
                  <span className="stat-label">エラー:</span>
                  <span className="stat-value">{result.result.errorCount}</span>
                </div>
              )}
            </div>

            {result.result.migrationWarning && (
              <div className="new-users-info" style={{ color: '#b45309' }}>
                <p>⚠ {result.result.migrationWarning}</p>
              </div>
            )}

            {result.result.byClass && result.result.byClass.length > 0 && (
              <div className="new-users-info">
                <p>授業ごとの結果</p>
                <table className="data-table" style={{ marginTop: '0.4rem' }}>
                  <thead>
                    <tr>
                      <th>授業名</th><th>履修登録</th><th>仮アカウント</th>
                      <th>氏名を更新</th><th>据え置き</th><th>スキップ</th><th>エラー</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.result.byClass.map((b, i) => (
                      <tr key={i}>
                        <td>
                          {b.className}
                          {b.isNew && (
                            <span style={{ marginLeft: '0.35rem', fontSize: '0.72rem',
                                           color: '#166534', fontWeight: 700 }}>新規</span>
                          )}
                          {b.otherYears && b.otherYears.length > 0 && (
                            <div style={{ fontSize: '0.75rem', color: '#b91c1c', fontWeight: 700 }}>
                              ⚠ 同じ名前の授業が {b.otherYears.join('、')} にもあります。
                              年度を間違えていたら、名簿の取り込み履歴の「年度を直す」で直せます
                            </div>
                          )}
                        </td>
                        <td>{b.enrolled}</td>
                        <td>{b.usersCreated}</td>
                        <td>{b.namesUpdated}</td>
                        <td>{b.namesKept}</td>
                        <td>{b.skipped}</td>
                        <td style={b.errorCount > 0 ? { color: '#c62828', fontWeight: 700 } : undefined}>
                          {b.errorCount}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {result.result.createdClasses && result.result.createdClasses.length > 0 && (
              <div className="new-users-info">
                <p>🆕 作成された授業</p>
                <ul className="new-users-list">
                  {result.result.createdClasses.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              </div>
            )}

            {result.result.renamedUsers && result.result.renamedUsers.length > 0 && (
              <div className="new-users-info">
                <p>表示名を名簿の氏名に更新しました（元の入力は記録として残しています）</p>
                <ul className="new-users-list">
                  {result.result.renamedUsers.slice(0, 10).map((u, i) => (
                    <li key={i}>{u.studentId}: {u.before} → {u.after}</li>
                  ))}
                  {result.result.renamedUsers.length > 10 && (
                    <li>...他 {result.result.renamedUsers.length - 10}名</li>
                  )}
                </ul>
              </div>
            )}

            {result.result.keptNames && result.result.keptNames.length > 0 && (
              <div className="new-users-info">
                <p>手動修正済みのため、名簿の氏名で上書きしなかった学生</p>
                <ul className="new-users-list">
                  {result.result.keptNames.slice(0, 10).map((u, i) => (
                    <li key={i}>{u.studentId}: {u.kept}（名簿: {u.csv}）</li>
                  ))}
                  {result.result.keptNames.length > 10 && (
                    <li>...他 {result.result.keptNames.length - 10}名</li>
                  )}
                </ul>
              </div>
            )}

            {result.result.autoMergedList && result.result.autoMergedList.length > 0 && (
              <div className="new-users-info">
                <p>既にサインアップ済みだったので、本人のアカウントに統合しました</p>
                <ul className="new-users-list">
                  {result.result.autoMergedList.slice(0, 10).map((m, i) => (
                    <li key={i}>
                      {m.studentId} {m.name} → ログインID {m.into}
                      （履修 {m.enrollmentsMoved}件 / 出席 {m.attendanceMoved}件を移動）
                    </li>
                  ))}
                  {result.result.autoMergedList.length > 10 && (
                    <li>...他 {result.result.autoMergedList.length - 10}件</li>
                  )}
                </ul>
              </div>
            )}

            {result.result.autoMergeAmbiguous > 0 && (
              <div className="new-users-info">
                <p>
                  同じ学籍番号の本アカウントが複数あるため自動統合しなかった仮アカウント:
                  {' '}{result.result.autoMergeAmbiguous}件
                </p>
                <p style={{fontSize:'0.85rem'}}>
                  学生管理 → 「仮アカウント統合」タブで、どちらに寄せるか選んでください。
                </p>
              </div>
            )}

            {result.result.errors && result.result.errors.length > 0 && (
              <div className="error-details">
                <p className="error-title">エラー詳細:</p>
                <ul className="error-list">
                  {result.result.errors.map((err, i) => (
                    <li key={i}>行{err.row}: {err.error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 結果表示 */}
      {result && importType !== 'manaba' && (
        <div className="alert alert-success">
          <span className="alert-icon"><Icon name="check" size={18}/></span>
          <div className="result-details">
            <p className="alert-message">{result.message}</p>
            <div className="result-stats">
              <div className="stat-item">
                <span className="stat-label">総レコード数:</span>
                <span className="stat-value">{result.totalRecords}</span>
              </div>
              <div className="stat-item success">
                <span className="stat-label">成功:</span>
                <span className="stat-value">{result.successCount}</span>
              </div>
              {result.updateCount !== undefined && (
                <div className="stat-item info">
                  <span className="stat-label">{result.skippedCount !== undefined ? '登録済み（変更なし）:' : '更新:'}</span>
                  <span className="stat-value">{result.updateCount}</span>
                </div>
              )}
              {result.skipCount !== undefined && (
                <div className="stat-item warning">
                  <span className="stat-label">スキップ:</span>
                  <span className="stat-value">{result.skipCount}</span>
                </div>
              )}
              {result.errorCount > 0 && (
                <div className="stat-item error">
                  <span className="stat-label">エラー:</span>
                  <span className="stat-value">{result.errorCount}</span>
                </div>
              )}
            </div>

            {result.newUsersCount > 0 && (
              <div className="new-users-info">
                <p>🆕 新規ユーザー: {result.newUsersCount}名</p>
                {result.newUsers && result.newUsers.length > 0 && (
                  <ul className="new-users-list">
                    {result.newUsers.slice(0, 5).map((user, index) => (
                      <li key={index}>
                        {user.studentId} - {user.name}
                      </li>
                    ))}
                    {result.newUsers.length > 5 && (
                      <li>...他 {result.newUsers.length - 5}名</li>
                    )}
                  </ul>
                )}
              </div>
            )}

            {result.errors && result.errors.length > 0 && (
              <div className="error-details">
                <p className="error-title">エラー詳細:</p>
                <ul className="error-list">
                  {result.errors.map((err, index) => (
                    <li key={index}>
                      行{err.row}: {err.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="reload-notice">
              3秒後にページを再読み込みします...
            </p>
          </div>
        </div>
      )}

      {/* 保存の確認【2026-09-11】。押す前に何が残るのかを見せる */}
      {file && SAVE_INFO[importType] && (
        <SaveConsent
          checked={saveOk}
          onChange={setSaveOk}
          title={SAVE_INFO[importType].title}
          items={SAVE_INFO[importType].items}
          note={SAVE_INFO[importType].note}
          defaultsOn={SAVE_INFO[importType].defaultOn}
        />
      )}

      {renderPreview()}

      {/* アップロードボタン */}
      <div className="upload-actions">
        <button
          className="btn-upload"
          onClick={() => handleUpload()}
          disabled={!file || uploading || !saveOk}
        >
          {uploading ? (
            <>
              <span className="spinner"></span>
              アップロード中...
            </>
          ) : (
            <>
              <Icon name="upload" size={16}/>
              {importType === 'manaba' ? (preview ? 'もう一度確認する' : '中身を確認する') : 'アップロード'}
            </>
          )}
        </button>

        {file && !uploading && (
          <button
            className="btn-clear"
            onClick={handleClear}
          >
            クリア
          </button>
        )}
      </div>
    </div>
  );
};

export default CSVImport;